// src/services/authService.js
import axiosInstance from './axiosInstance';

let csrfBootstrapped = false;

async function ensureCsrf() {
  if (csrfBootstrapped) return;
  
  try {
    const res = await axiosInstance.get('/auth/csrf');
    
    // Check if the cookie was set
    const cookies = document.cookie.split(';');
    const xsrfCookie = cookies.find(cookie => cookie.trim().startsWith('XSRF-TOKEN='));
    
    if (xsrfCookie) {
      csrfBootstrapped = true;
    } else {
      csrfBootstrapped = false;
    }
  } catch (error) {
    csrfBootstrapped = false;
    throw error;
  }
}

const registerUser = async (user) => {
  await ensureCsrf();
  const response = await axiosInstance.post(`/auth/register`, user);
  return response.data;
};

const loginUser = async (user) => {
  await ensureCsrf();
  const response = await axiosInstance.post(`/auth/login`, user);
  return response.data;
};

const loginWithGoogle = async (googleToken) => {
  const response = await axiosInstance.post(`/auth/google-login`, { 
    token: googleToken 
  });
  return response.data;
};

const forgotPassword = async (email) => {
  await ensureCsrf();
  const response = await axiosInstance.post(`/auth/password/forgot`, { email });
  return response.data;
};

const resetPassword = async (token, newPassword) => {
  await ensureCsrf();
  const response = await axiosInstance.post(`/auth/password/reset`, { token, newPassword });
  return response.data;
};

const updateUsername = async (newUsername) => {
  await ensureCsrf();
  const response = await axiosInstance.post(`/auth/update-username`, { username: newUsername });
  return response.data;
};

const logout = async () => {
  try {
    await axiosInstance.post('/auth/logout');
    csrfBootstrapped = false;
    
    return { success: true };
  } catch (error) {
    console.error('Server logout failed:', error);
    
    csrfBootstrapped = false;
    
    clearAuthCookies();
    
    return { 
      success: false, 
      error: error.message,
      localCleanup: true 
    };
  }
};

const logoutAllDevices = async () => {
  await ensureCsrf();
  try {
    await axiosInstance.post('/auth/logout-all');
    csrfBootstrapped = false;
    return { success: true };
  } catch (error) {
    console.error('Server logout-all failed:', error);
    csrfBootstrapped = false;
    clearAuthCookies();
    return { success: false, error: error.message, localCleanup: true };
  }
};

const clearAuthCookies = () => {
  const cookiesToClear = ['ACCESS-TOKEN', 'REFRESH-TOKEN', 'XSRF-TOKEN'];
  
  cookiesToClear.forEach(cookieName => {
    const paths = ['/', '/api', '/api/auth'];
    paths.forEach(path => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; secure; samesite=strict`;
    });
  });
};

const checkAuthStatus = async () => {
  try {
    const response = await axiosInstance.get('/auth/me');
    return { isAuthenticated: true, user: response.data };
  } catch (error) {
    return { isAuthenticated: false, error: error.message };
  }
};

export default { 
  registerUser, 
  loginUser, 
  loginWithGoogle, 
  forgotPassword, 
  resetPassword, 
  updateUsername,
  logout,
  logoutAllDevices,
  checkAuthStatus,
  clearAuthCookies
};